import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, DestroyRef, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { TranslateModule } from '@ngx-translate/core'
import { ItemsFacade } from '@galabid/gb-dashboard/item/data-access'
import { GbDashboardItemUiModule } from '@galabid/gb-dashboard/item/ui'
import {
  GbDashboardSharedFeatureModule,
  GbTableColumn,
  GbTableItem,
  ColumnTypeEnum,
  GbTableColumnTemplate,
} from '@galabid/gb-dashboard/shared/feature'
import { combineLatest, distinctUntilChanged, map, of, shareReplay, take } from 'rxjs'
import * as ItemsActions from 'libs/gb-dashboard/item/data-access/src/lib/+state/items.actions'

export interface GbPresenterExcludedItemsDialogData {
  hiddenItemUuids: string[]
}

@Component({
  selector: 'gb-presenter-excluded-items-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    TranslateModule,
    GbDashboardSharedFeatureModule,
    GbDashboardItemUiModule,
  ],
  templateUrl: './gb-presenter-excluded-items-dialog.component.html',
  styleUrl: './gb-presenter-excluded-items-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterExcludedItemsDialogComponent implements OnInit {
  hiddenItemUuids: string[] = []

  loaded$ = this.itemsFacade.loaded$
  count$ = this.itemsFacade.count$

  private excludedItems$ = this.itemsFacade.allItems$.pipe(
    map((items) => {
      const hiddenSet = new Set(this.hiddenItemUuids)
      return (items || []).filter((item) => hiddenSet.has(item.uuid)).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  tableData$ = this.excludedItems$.pipe(
    map((items): GbTableItem[] =>
      items.map((item) => ({
        uuid: item.uuid,
        name: item.name,
        alias: item.alias,
        item_type: item.item_type,
      })),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  displayedColumns$ = of<GbTableColumn[]>([
    {
      columnKey: 'name',
      columnLabel: 'NAME',
      columnType: ColumnTypeEnum.Label,
    },
    {
      columnKey: 'alias',
      columnLabel: 'ITEMS.IOC_SHOP.ALIAS',
      columnType: ColumnTypeEnum.Label,
      disableSort: true,
    },
    {
      columnKey: 'item_type',
      columnLabel: 'ITEMS.LIST.TYPE',
      columnType: ColumnTypeEnum.Template,
    },
  ])

  vm$ = combineLatest([this.excludedItems$, this.loaded$, this.count$]).pipe(
    map(([items, loaded, count]) => ({ items, loaded, count })),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  constructor(
    private itemsFacade: ItemsFacade,
    private dialogRef: MatDialogRef<GbPresenterExcludedItemsDialogComponent>,
    private destroyRef: DestroyRef,
    @Inject(MAT_DIALOG_DATA) public data: GbPresenterExcludedItemsDialogData,
  ) {}

  ngOnInit(): void {
    this.hiddenItemUuids = [...(this.data.hiddenItemUuids || [])]

    this.count$.pipe(take(1)).subscribe((count) => {
      if (count === 0) {
        this.itemsFacade.dispatch(ItemsActions.loadItems({ projector: true, limit: 20 }))
      }
    })

    this.loaded$.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe()
  }

  getDisplayedColumns$(itemType: TemplateRef<GbTableColumnTemplate>) {
    return this.displayedColumns$.pipe(
      map((columns) =>
        columns.map((column) => (column.columnKey === 'item_type' ? { ...column, columnTemplate: itemType } : column)),
      ),
    )
  }

  close(): void {
    this.dialogRef.close()
  }
}
